/**
 * Component used to add a simple sign on authentication flow via modals to any component, for example during checkout.
 */

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import * as React from 'react';

import { ErrorResponse } from 'legacy/common/utils/error';
import { LoginResponse } from 'legacy/auth/types';
import LoginAuthenticationFlow from './LoginAuthenticationFlow';
import SignUpAuthenticationFlow from './SignUpAuthenticationFlow';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import { useMediaQuery } from 'theme/useMediaQuery';
import { Breakpoints, BreakpointsKeys } from 'theme/types';

enum AuthFlow {
  SIGN_UP,
  LOGIN
}

type AuthenticationFlowProps = {
  onCompleteAuthentication?: (loginResponse: LoginResponse) => void;
  onFailedAuthentication?: (error: ErrorResponse) => void;
  children: ({
    startAuthenticationFlow
  }: {
    startAuthenticationFlow: () => void;
  }) => React.ReactNode;
};
const AuthenticationFlow = ({
  onCompleteAuthentication,
  onFailedAuthentication,
  children
}: AuthenticationFlowProps): any => {
  const [selectedAuthFlow, setSelectedAuthFlow] = React.useState<AuthFlow>(
    AuthFlow.SIGN_UP
  );
  const [isModalVisible, setIsModalVisible] = React.useState(false);

  // Use full screen on mobile (below tablet breakpoint), small on desktop
  const isMobile = useMediaQuery(
    `(max-width: ${Breakpoints[BreakpointsKeys.Tablet] - 1}px)`
  );
  const modalType = isMobile ? ModalType.FULL_SCREEN : ModalType.SMALL;

  const renderModalContent = () => {
    if (selectedAuthFlow === AuthFlow.SIGN_UP) {
      return (
        <SignUpAuthenticationFlow
          onClickLogin={() => setSelectedAuthFlow(AuthFlow.LOGIN)}
          onCompleteSignUp={(loginResponse) => {
            onCompleteAuthentication(loginResponse);
            setIsModalVisible(false);
          }}
          onFailedSignUp={onFailedAuthentication}
        />
      );
    }

    return (
      <LoginAuthenticationFlow
        onClickSignUp={() => setSelectedAuthFlow(AuthFlow.SIGN_UP)}
        onCompleteLogin={(loginResponse) => {
          onCompleteAuthentication(loginResponse);
          setIsModalVisible(false);
        }}
        onFailedLogin={onFailedAuthentication}
      />
    );
  };

  return (
    <>
      {children({
        startAuthenticationFlow: () => {
          setIsModalVisible(true);
        }
      })}

      <Modal
        disableAnimationOnWeb
        type={modalType}
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
      >
        <div className="overflow-y-auto">
          <FadeIn>{renderModalContent()}</FadeIn>
        </div>
      </Modal>
    </>
  );
};

export default AuthenticationFlow;
